import { useState } from 'react';
import { useFormikContext } from 'formik';
import { AddSquare, CloseCircle } from 'iconsax-react';

import { Input } from '@src/design-system/input';
import { Label } from '@src/design-system/label';
import { Button } from '@src/design-system/button';
import { TYPES_WITH_OPTIONS } from '../custom-field-form';
import { CustomErrorMessage } from '@src/design-system/error-message';
import type { TCustomFieldsFormValues } from '../custom-field-form';

export function CustomFieldOptionsManager({
  canRemoveOptions,
}: {
  canRemoveOptions: boolean;
}) {
  const {
    values,
    setFieldValue,
    errors,
    touched,
    setFieldError,
    setFieldTouched,
  } = useFormikContext<TCustomFieldsFormValues>();
  const [inputValue, setInputValue] = useState('');

  const handleAddOption = async () => {
    if (!inputValue) return;

    const isDuplicate = values.options.some(
      (option) => option.toLowerCase() === inputValue.toLowerCase()
    );
    if (isDuplicate) {
      setTimeout(() => setFieldError('options', 'This option already exists'));
      setFieldTouched('options', true, true);
      return;
    }

    setFieldValue('options', [...values.options, inputValue]);
    setInputValue('');
  };

  const handleDeleteOption = (index: number) => {
    setFieldValue(
      'options',
      values.options.filter((_, i) => i !== index)
    );
  };

  if (!TYPES_WITH_OPTIONS.includes(values.type.value)) return null;

  return (
    <div className='flex flex-col gap-2'>
      <div>
        <Label>Add multi choices</Label>
        <div className='flex w-full gap-2'>
          <Input
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setFieldError('options', undefined);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddOption();
              }
            }}
            placeholder='Enter option'
            className='flex-1 text-sm'
          />
          <Button onClick={handleAddOption}>
            <AddSquare className='size-4' /> Add
          </Button>
        </div>
      </div>

      {errors.options && touched.options && (
        <CustomErrorMessage className='-mt-[2px]'>
          {errors.options}
        </CustomErrorMessage>
      )}

      {values.options.length > 0 && (
        <div>
          <Label>Options</Label>
          <div className='flex flex-wrap gap-2'>
            {values.options.map((option, index) => (
              <span
                key={index}
                className='flex items-center gap-1 border border-pale-border transition-colors hover:border-primary-300 rounded-md px-2 py-1 text-sm'
              >
                {canRemoveOptions && (
                  <CloseCircle
                    onClick={() => handleDeleteOption(index)}
                    className='size-4 cursor-pointer opacity-60 hover:opacity-100'
                  />
                )}
                {option}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
